/**
 * LoginV2 — redesigned CMS sign-in.
 *
 * Same auth flow as the v1 (Google Workspace OAuth via /api/v1/auth/login,
 * email/password fallback via emailLogin action) — only the presentation
 * changes. Workspace SSO is the loud primary CTA; the email option is
 * deliberately demoted to a small "Use email instead" link beneath it.
 *
 * Wrapped in a single full-bleed card on a soft brand-gradient background.
 */
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import authConfig from '../../config/auth';
import { emailLogin } from '../../actions/authActions';
import { Button, TtsSwirl, cn } from '../redesign';

const GoogleGlyph = (
  // Multi-color Google "G" — the universally recognized brand mark. Lifted
  // verbatim from the prototype's login button SVG.
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path fill="#4285F4" d="M22.6 12.2c0-.7-.1-1.4-.2-2.1H12v4h6c-.3 1.4-1 2.5-2.2 3.3v2.8h3.6c2.1-1.9 3.2-4.8 3.2-8z"/>
    <path fill="#34A853" d="M12 23c3 0 5.5-1 7.3-2.7l-3.6-2.8c-1 .7-2.3 1.1-3.7 1.1-2.8 0-5.2-1.9-6.1-4.5H2.2v2.8C4 19.9 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.9 14.1A6.6 6.6 0 0 1 5.5 12c0-.7.1-1.4.3-2.1V7.1H2.2A11 11 0 0 0 1 12c0 1.8.4 3.5 1.2 5l3.7-2.9z"/>
    <path fill="#EA4335" d="M12 5.4c1.6 0 3 .6 4.1 1.6L19.2 4C17.5 2.4 14.9 1 12 1 7.7 1 4 3.6 2.2 7.1l3.7 2.8C6.8 7.3 9.2 5.4 12 5.4z"/>
  </svg>
);

const EmailGlyph = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <rect x="3" y="5" width="18" height="14" rx="2"/>
    <path d="m3 7 9 6 9-6"/>
  </svg>
);

export default function LoginV2() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(searchParams.get('error') || '');
  const [showEmail, setShowEmail] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [emailLoading, setEmailLoading]   = useState(false);

  const handleWorkspaceLogin = () => {
    setGoogleLoading(true);
    const params = new URLSearchParams({
      client_id: authConfig.clientId,
      redirect_uri: authConfig.callbackUrl,
      idp: 'GoogleWorkspace',
    });
    window.location.href = `${authConfig.ttsAuthUrl}/api/v1/auth/login?${params}`;
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError('');
    setEmailLoading(true);
    try {
      await dispatch(emailLogin({ email, password }));
      navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setEmailLoading(false);
    }
  };

  return (
    // Full-bleed brand-gradient background so the card floats in TTS color.
    <div
      className={cn(
        'fixed inset-0 grid place-items-center overflow-auto p-6 bg-bg',
        // soft gradient — matches the prototype's login-screen radial blend.
        // The gradient itself is a theme-aware token (--login-aura) so it
        // dims to brand-tinted glows in dark mode instead of blowing out
        // into bright light-mode washes.
      )}
      style={{ backgroundImage: 'var(--login-aura)' }}
    >
      {/* Corner swirl watermarks — purely decorative. Pointer-events-none so
          they never block clicks; opacity-10 so the brand mark on the card
          stays the focal point. Positioned partly off-canvas for the
          "wallpaper" feel from the prototype. */}
      <TtsSwirl
        className="pointer-events-none absolute opacity-10 select-none"
        style={{
          top: -40, left: -40,
          width: 280, height: 256,
          transform: 'rotate(-12deg)',
        }}
        aria-hidden
      />
      <TtsSwirl
        className="pointer-events-none absolute opacity-10 select-none"
        style={{
          bottom: -60, right: -50,
          width: 360, height: 330,
          transform: 'rotate(160deg)',
        }}
        aria-hidden
      />

      <div
        className={cn(
          'relative w-full max-w-md bg-surface border border-sand rounded-xl',
          'p-9 pt-10 pb-7',
        )}
        style={{ boxShadow: 'var(--login-card-shadow)' }}
      >
        {/* Brand mark — the TTS swirl SVG (replaces the legacy top-ts-logo.jpg
            which was a low-res photographic asset). Layout mirrors the
            prototype: swirl → brand wordmark → "Content Management" eyebrow. */}
        <div className="flex flex-col items-center mb-5">
          <TtsSwirl className="w-16 h-14 mb-3" />
          <h1 className="font-condensed font-bold text-navy text-[22px] leading-none tracking-tight">
            The Tapping Solution
          </h1>
          <p className="mt-1 font-condensed font-semibold text-text-muted text-[11px] uppercase tracking-widest">
            Content Management
          </p>
        </div>

        {/* Welcome hero — "Welcome back" with the second word in coral italic
            to match the prototype's accent treatment. */}
        <div className="text-center mb-6">
          <h2 className="font-condensed font-bold text-navy text-[24px] leading-tight tracking-tight">
            Welcome <em className="italic font-bold text-coral">back</em>
          </h2>
          <p className="text-text-light text-[13px] mt-1.5">
            Sign in to manage sessions, audiobooks, and challenges.
          </p>
        </div>

        {/* Top-of-form error — only when NOT showing email form (the email form
            has its own error slot just above its fields). */}
        {error && !showEmail && (
          <div className="mb-4 px-3 py-2 rounded-md bg-coral-soft border border-coral/40 text-coral-dark text-[12.5px]">
            {error}
          </div>
        )}

        {/* Primary CTA — Workspace SSO. Default-variant outlined pill with the
            full multi-color Google G inside, matching the prototype design.
            The recognisable Google branding does the work of signalling
            primacy; the teal email button below is secondary by position. */}
        <Button
          variant="default"
          size="md"
          onClick={handleWorkspaceLogin}
          disabled={googleLoading}
          leadingIcon={GoogleGlyph}
          className="w-full h-12 text-base font-semibold"
        >
          {googleLoading ? 'Redirecting to Workspace…' : 'Sign in with Google Workspace'}
        </Button>

        <p className="mt-3 text-center text-text-muted text-[11.5px]">
          Use your <span className="text-navy font-semibold">@thetappingsolution.com</span> account.
        </p>

        {/* Email fallback — secondary CTA (teal). Hierarchy is by color, not
            size: coral is louder than teal in the brand palette, so the
            Workspace button still reads as the primary action. The line of
            copy below the button keeps the soft nudge toward SSO. */}
        {!showEmail ? (
          <div className="mt-6 pt-5 border-t border-line">
            <Button
              variant="primary"
              size="md"
              onClick={() => setShowEmail(true)}
              leadingIcon={EmailGlyph}
              className="w-full h-12 text-base font-semibold"
            >
              Sign in with email
            </Button>
            <p className="mt-2 text-center text-text-light text-[11px]">
              Service accounts only. Workspace SSO is preferred.
            </p>
          </div>
        ) : (
          // Expanded email form — matches the prototype's .login-form section:
          // uppercase Asap Condensed labels with letter-spacing, cream-bg inputs
          // with sand border, focus ring in teal-soft, full-width "Sign in"
          // primary submit. Layout: gap-3 grid (12px) per the original.
          //
          // Animation: the container fades + slides in (240ms), and the form
          // children stagger by 60ms each so the eye walks down the form
          // instead of seeing everything land at once.
          <div className="mt-6 animate-[fadeSlideIn_240ms_ease-out]">
            <div className="mb-3">
              <span className="font-condensed font-bold text-text-muted text-[11px] uppercase tracking-wider">
                Sign in with email
              </span>
            </div>

            {error && (
              <div className="mb-3 px-3 py-2 rounded-md bg-coral-soft border border-coral/40 text-coral-dark text-[12.5px] animate-[fadeIn_220ms_ease-out]">
                {error}
              </div>
            )}

            <form
              onSubmit={handleEmailLogin}
              className="grid gap-3 stagger-children"
              style={{ '--stagger': '70ms' }}
            >
              {/* Field block — Email */}
              <div style={{ '--i': 1 }}>
                <label
                  htmlFor="login-email"
                  className="block font-condensed font-bold text-[11px] uppercase tracking-wider text-text-light mb-1.5"
                >
                  Email
                </label>
                <input
                  id="login-email"
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="kelly@thetappingsolution.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={cn(
                    'w-full px-3 py-2.5 rounded-md border border-sand bg-cream',
                    'text-[13.5px] text-navy outline-none transition-all duration-150',
                    'focus:bg-surface focus:border-teal-green',
                    'focus:ring-4 focus:ring-teal-green-soft',
                  )}
                />
              </div>

              {/* Field block — Password */}
              <div style={{ '--i': 2 }}>
                <label
                  htmlFor="login-password"
                  className="block font-condensed font-bold text-[11px] uppercase tracking-wider text-text-light mb-1.5"
                >
                  Password
                </label>
                <input
                  id="login-password"
                  type="password"
                  required
                  autoComplete="current-password"
                  placeholder="••••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={cn(
                    'w-full px-3 py-2.5 rounded-md border border-sand bg-cream',
                    'text-[13.5px] text-navy outline-none transition-all duration-150',
                    'focus:bg-surface focus:border-teal-green',
                    'focus:ring-4 focus:ring-teal-green-soft',
                  )}
                />
              </div>

              <Button
                type="submit"
                variant="primary"
                size="md"
                disabled={emailLoading}
                className="w-full h-[42px] text-[14px] mt-1"
                style={{ '--i': 3 }}
              >
                {emailLoading ? 'Signing in…' : 'Sign in'}
              </Button>
            </form>
          </div>
        )}

      </div>
    </div>
  );
}
